import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import LoadingContainer from "../../components/containers/loadingContainer";
import { RoutePointStatus } from "../../models/enums/routePointStatus";
import { RoutePoint } from "../../models/routes/routePoint";
import routePointsService from "../../services/routePointsService";
import routesService from "../../services/routesService";
import CheckList from "./checkList";
import RoutePointView from "./routPointView";
import { NextWorksIds, Route } from "../../models/routes/route";
import { RouteStatus } from "../../models/enums/routeStatus";
import androidService from "../../services/mobile/androidService";
import { NextWorkFlowCard } from "./nextWorkFlowCard";
import { Button } from "reactstrap";
import RouteNavigationModal from "./routeNavigationModal";
import { useAppSelector } from "../../hooks";

// Маршрут считается активным для трекинга от старта работы и до момента, когда
// он переходит к ожиданию подтверждения завершения (там сервис явно останавливается).
const isLocationTrackingActive = (status: RouteStatus | undefined) =>
    status === RouteStatus.InWork || status === RouteStatus.ParcelPickedUp || status === RouteStatus.WaitingForRouteSheet;

export default function RoutePointsPage() {
    const { routeId } = useParams();
    const navigate = useNavigate();
    const accessToken = useAppSelector((state) => state.auth.accessToken)!;

    const [route, setRoute] = useState<Route | null>(null);
    const [routePoints, setRoutePoints] = useState<RoutePoint[]>([]);
    const [openPoint, setOpenPoint] = useState<RoutePoint | null>(null);
    const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);

    // Возвращаемся к списку маршрутов, когда маршрут завершается в этой сессии.
    const prevStatusRef = useRef<RouteStatus | undefined>(undefined);
    useEffect(() => {
        const prev = prevStatusRef.current;
        prevStatusRef.current = route?.status;

        if (prev !== undefined && prev !== RouteStatus.Finished && route?.status === RouteStatus.Finished) navigate("/");
    }, [route?.status, navigate]);

    // Подхватываем трекинг, если пользователь сначала отказал в разрешении, а затем
    // выдал его через настройки телефона. Дополнительно — при возврате приложения
    // на передний план: visibilitychange срабатывает после системного экрана настроек.
    useEffect(() => {
        if (!routeId || !isLocationTrackingActive(route?.status)) return;

        const tryStart = () => androidService.startLocationServiceIfPermitted(routeId, accessToken);
        tryStart();

        const onVisible = () => {
            if (document.visibilityState === "visible") tryStart();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [routeId, accessToken, route?.status]);

    const setCurrentPointStatus = (points: RoutePoint[]) => {
        // Делаем доступной следующую точку маршрута
        const nextPoint = points.find((p) => p.status === RoutePointStatus.Open);
        if (nextPoint !== undefined) nextPoint.status = RoutePointStatus.Current;

        setRoutePoints(points);
    };

    const loadRoute = async () => {
        const routeResponse = await routesService.getRoute(routeId!);
        const newPoints = routeResponse.isOk ? routeResponse.data!.points : [];
        routeResponse.isOk && setRoute(routeResponse.data!);
        setCurrentPointStatus(newPoints);
    };

    const handleDoneButtonClick = async () => {
        const index = routePoints.findIndex((p) => p.orderId === openPoint!.orderId);
        const point = routePoints[index];

        const { isOk, data } = await routePointsService.setStatus(point.orderId, RoutePointStatus.Closed);
        const newPoints = [...routePoints];
        if (isOk && data) {
            newPoints[index] = data;
            setCurrentPointStatus(newPoints);
        }

        // Если это первая точка, то ставим родительский заказ в статус ожидает сдачи маршрутного листа
        if (index === 0 && route!.nextWorksCheckListId && NextWorksIds.includes(route!.nextWorksCheckListId)) {
            const { isOk: isUpdateRoute, data: updatedRoute } = await routesService.setStatus(
                point.orderId,
                RouteStatus.ParcelPickedUp,
            );
            isUpdateRoute && setRoute({ ...route!, status: updatedRoute!.status });
        }

        // Если это последняя точка, то завершаем отслеживание маршрута
        if (newPoints.every((p) => p.status >= RoutePointStatus.Closed)) await handleFinishRoute();
        handleCloseCheckList();
    };

    const handleRejectButtonClick = async (reason: string) => {
        const index = routePoints.findIndex((p) => p.orderId === openPoint!.orderId);
        const point = routePoints[index];

        const { isOk, data } = await routePointsService.setStatus(point.orderId, RoutePointStatus.Rejected, reason);
        const newPoints = [...routePoints];
        if (isOk && data) {
            newPoints[index] = data;
            setCurrentPointStatus(newPoints);
        }

        // Если это последняя точка, то завершаем отслеживание маршрута
        if (newPoints.every((p) => p.status >= RoutePointStatus.Closed)) await handleFinishRoute();
        handleCloseCheckList();
    };

    const handleOpenPoint = (point: RoutePoint) => {
        setOpenPoint(point);
    };

    const handleCloseCheckList = () => setOpenPoint(null);

    const handleChangeOpenPoint = (point: RoutePoint) => {
        setOpenPoint(point);
        const index = routePoints.findIndex((p) => p.orderId === point.orderId);
        const newPoints = [...routePoints];
        newPoints[index] = point;
        setRoutePoints(newPoints);
    };

    const handleFinishRoute = async () => {
        const { isOk, data } = await routesService.setStatus(routeId!, RouteStatus.WaitingForFinishConfirmation);
        if (isOk && data) {
            setRoute(data);
            androidService.stopLocationService();
        }
    };

    const isReadyForFinishAction = routePoints.every((p) => p.status >= RoutePointStatus.Closed);
    const isFirstPointFinished = routePoints[0]?.status >= RoutePointStatus.Closed;

    return (
        <div>
            {!openPoint && <h6 className="text-center fw-bold">Маршрут - {routeId}</h6>}
            <LoadingContainer onLoading={loadRoute} caption="Загрузка маршрута">
                {openPoint ? (
                    <CheckList
                        routePoint={openPoint}
                        isFirstPoint={openPoint?.orderId === routePoints[0].orderId}
                        onDoneButtonClick={handleDoneButtonClick}
                        onRejectButtonClick={handleRejectButtonClick}
                        onClose={handleCloseCheckList}
                        onChangePoint={handleChangeOpenPoint}
                    />
                ) : route ? (
                    <>
                        <Button color="primary" className="w-100 mb-3" onClick={() => setIsRouteModalOpen(!isRouteModalOpen)}>
                            Построить маршрут
                        </Button>

                        <RouteNavigationModal
                            isOpen={isRouteModalOpen}
                            toggle={() => setIsRouteModalOpen(!isRouteModalOpen)}
                            routePoints={routePoints}
                        />

                        {routePoints.map((point, index) => (
                            <RoutePointView
                                key={index}
                                routePoint={point}
                                route={route}
                                number={index + 1}
                                onOpenPoint={() => handleOpenPoint(point)}
                                isFirstPointFinished={isFirstPointFinished}
                            />
                        ))}
                        <NextWorkFlowCard
                            route={route}
                            onChangeRoute={setRoute}
                            isReadyForFinishAction={isReadyForFinishAction}
                        />
                    </>
                ) : (
                    <div>Маршрут {routeId} не найден</div>
                )}
            </LoadingContainer>
            {!openPoint && (
                <Link to="/" className="btn btn-secondary btn-lg w-100 mt-5 mb-4">
                    К списку маршрутов &lt;&lt;
                </Link>
            )}
        </div>
    );
}
